import React, { useEffect, useState, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import qs from "qs";
import moment from 'moment';
import { cloneDeep, isArray, chain, isEmpty, isUndefined, isNull, toUpper } from 'lodash';
import { saveAs } from 'file-saver';
import { pdf, Document } from '@react-pdf/renderer';
import AppContext from '../../../app/context';
import useApi from '../../../hooks/useApi';
import useAccountSettings from '../../../hooks/useAccountSettings';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { Row, Col, Dropdown, Menu, Grid, Modal, Image, Pagination, Form, Checkbox, Input, notification } from 'antd';
import { EditOutlined, DeleteOutlined, SearchOutlined} from '@ant-design/icons';
import { MdOutlineMoreHoriz, MdFilterList, MdOutlineClose, MdChevronLeft, MdChevronRight, MdChecklist, MdOutlineSettings } from "react-icons/md";
import { getAdminUsers, deleteEvent, getEventsV2, getAccountEventStagesSummary, updateEventStage } from '../../../api';
import LoadingSpinner from '../../../components/loading';
import AdminContent from '../../../components/adminContent';
import ErrorCard from '../../../components/errorCard';
import EventCard from '../../../components/eventCard';
import StatusTag from '../../../components/statusTag';
import Permissions from '../../../constants/permissions';
import { hasPermission } from '../../../helpers/permissionHelper';
import { getEventFilters, setEventFilters } from '../../../helpers/eventHelper';
import { formatEventDateLong, formatEventDayMonth } from '../../../helpers/dateHelper';
import emptyStateImage from '../../../images/empty-state-icon.png';
import FloatingContainer from '../../../components/floatingContainer'
import { LoadingOutlined, UserOutlined} from '@ant-design/icons';
import { HiOutlineViewList } from "react-icons/hi";
import { MdOutlineGridView } from "react-icons/md";
import { FiDownload } from "react-icons/fi";
import {
  renderSelectField
} from '../../../components/formFields'
import Roles from '../../../constants/roles';
import EventListPDF from '../../../pdfs/eventListPDF';

const EventsPage = () => {

  const { auth, setAuth } = useContext(AppContext);
  const user = auth.user ? auth.user : {}
  const canViewAllEvents = hasPermission(user, Permissions.VIEW_ALL_EVENTS);
  const canCreateEvents = hasPermission(user, Permissions.CREATE_EVENTS)
  const canEditEventDetails = hasPermission(user, Permissions.EDIT_EVENT_DETAILS)
  const canViewEventStageSettings = auth.user.role == Roles.OWNER

  useDocumentTitle("Events")
  const navigate = useNavigate();
  const location = useLocation()
  const [sendRequest] = useApi()
  const [accountSettings] = useAccountSettings()

  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();

  const [editStageForm] = Form.useForm();

  const [isLoading, setLoading] = useState(true);
  const [isContentLoading, setContentLoading] = useState(true);
  const [isError, setError] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [events, setEvents] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedAssigned, setSelectedAssigned] = useState();
  const [isFilterOpen, setFilterOpen] = useState(false);
  const [isConfirmRemoveVisible, setConfirmRemoveVisible] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState({});
  const [isDeleting, setDeleting] = useState(false);
  const [isDeleteError, setDeleteError] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [limit, setLimit] = useState(50);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [eventStages, setEventStages] = useState([]);
  const [eventStagesData, setEventStagesData] = useState([]);
  const [hiddenStageCount, setHiddenStageCount] = useState(0); 
  const [selectedStage, setSelectedStage] = useState("all");
  const [tableDisplay, setTableDisplay] = useState("grid");
  const [isUpdateStageModalVisible, setUpdateStageModalVisible] = useState(false);
  const [sliderWidth, setSliderWidth] = useState(window.innerWidth);
  const [selectedStaff, setSelectedStaff] = useState([]);
  const [tempSelectedStaff, setTempSelectedStaff] = useState([]);
  const [showHiddenStages, setShowHiddenStages] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setSliderWidth(getNewSliderWidth())
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getNewSliderWidth = () => {
    const innerWidth = window.innerWidth
    var newWidth = 0
    if (innerWidth < 768) {
      newWidth = innerWidth - 40
    } else if (innerWidth < 1300) {
      newWidth = innerWidth - 295
    } else {
      newWidth = 1000
    }
    return newWidth
  }

  useEffect(() => {
    window.scrollTo(0, 0);
    const eventFilters = getEventFilters(auth.user.user_id)
    const queryStrings = qs.parse(location.search, { ignoreQueryPrefix: true })

    var appliedStage = null;
    if (queryStrings.stage) {
      appliedStage = queryStrings.stage;
    } else if (eventFilters.stage) {
      appliedStage = eventFilters.stage;
    } else {
      appliedStage = "all"
    }

    var appliedStaff = [];
    if (eventFilters.selected_staff) {
      appliedStaff = eventFilters.selected_staff
    }

    if (eventFilters.table_display) {
      setTableDisplay(eventFilters.table_display)
    }

    updateResults(appliedStage, appliedStaff, 0)
    setSelectedStaff(appliedStaff)   
    refreshPage(appliedStage)
    setSliderWidth(getNewSliderWidth())
  }, []);

  const refreshPage = async (stage) => {
    try {
      if (canViewAllEvents) {
        const employeeResults = await sendRequest(getAdminUsers());
        setEmployees(employeeResults)
      }

      await refreshEventStages(showHiddenStages)

      if (!isUndefined(stage)) {
        setTimeout(() => {
          slideToElement(stage)
        }, 200)
      }
    } catch {
      setError(true)
    } finally {
      setLoading(false)
    }
  }

  const refreshEventStages = async (showHidden = false) => {
    try {
      const filter = showHidden ? "all" : "active";
      const eventStageResults = await sendRequest(getAccountEventStagesSummary(filter))
      const filteredEventStages =  eventStageResults.filter(x => x.stage_id == "all" || x.display || showHidden)
      const hiddenEventStages =  eventStageResults.filter(x => x.stage_id != "all" && !x.display)
      setEventStages(filteredEventStages)
      setHiddenStageCount(hiddenEventStages.length)

      const eventStageDataResults = eventStageResults.filter(x => x.stage_id != "all" ).map(x => {
        return {
          text: `${x.stage_name}${!x.display ? " (Hidden)" : ""}`,
          value: x.stage_id
        }
      })
      setEventStagesData(eventStageDataResults)

      // Confirmed saved stage filter is in the list of displayed stages
      const eventFilters = getEventFilters(auth.user.user_id)
      if (eventFilters.stage != "all") {
        const foundStage = eventStageResults.find(x => x.stage_id == eventFilters.stage && x.display)
        if (isEmpty(foundStage)) {
          updateResults("all", selectedStaff, 0)
          onUpdateEventFilters("all", tableDisplay, selectedStaff)
        }
      }
    } catch {
      setError(true)
    }
  }

  const isValid = (value) => {
    return !isNull(value) && !isUndefined(value) && !isEmpty(value)
  }

  const updateResults = async (stage, staff, offset, showHidden, pageLimit) => {
    try {
      // Set loading
      setContentLoading(true)

      // Motify filter
      const filteredStaff = staff == 1 || isUndefined(staff) ? [] : staff
      const finalShowHidden = isUndefined(showHidden) ? showHiddenStages : showHidden
      const finalPageLimit = isUndefined(pageLimit) ? limit : pageLimit

      // Set state variables
      setSelectedStage(stage)
      setSelectedStaff(filteredStaff)
      setCurrentOffset(offset)
      updateUrl(stage, staff)

      const body = {
        stage: stage,
        offset: offset,
        limit: finalPageLimit,
        staff: filteredStaff,
        show_hidden: finalShowHidden
      }
      // Fetch records
      const results = await sendRequest(getEventsV2(body))
      const mappedEventsWithMonthAndYear = results.results.map(x => {
        const eventDate = moment(x.event_date, "YYYY-MM-DD")
        var monthYear = eventDate.format("MMMM YYYY").toUpperCase()
        return {
          ...x,
          month_year: monthYear
        }
      })
      setEvents(mappedEventsWithMonthAndYear)
      setTotalRecords(results.metadata.total)
    } catch (error) {
      console.log(error)
    } finally {
      setContentLoading(false)
      setLoading(false)
    }
  }

  const removeEvent = (event) => {
    setConfirmRemoveVisible(true)
    setDeleteError(false)
    setSelectedEvent(event)
  }

  const confirmDeleteEvent = async () => {
    setDeleteError(false)
    try {
      if (selectedEvent.event_id) {
        setDeleting(true)
        const deleteResults = await sendRequest(deleteEvent(selectedEvent.event_id));
        if (deleteResults.success) {
          await refreshEventStages(showHiddenStages)
          await updateResults(selectedStage, selectedStaff, currentOffset)
          setConfirmRemoveVisible(false)
        } else {
          setDeleteError(true)
        }
        setDeleting(false)
      } else {
        setDeleteError(true)
      }
    } catch (error) {
      setDeleteError(true)
      setDeleting(false)
    }
  }

  const getGroupedEvents = () => {
    return chain(events).groupBy("month_year").map((value, key) => ({ label: key, events: value})).value()
  }

  const updateUrl = (stage, staff) => {
    var url = "/admin/events"
    var params = {}

    if (isValid(stage)) {
      params.stage = stage
    }

    // if (isValid(staff)) {
    //   params.staff = staff
    // }

    // Stringify params
    const paramString = qs.stringify(params)

    if (!isEmpty(params)) {
      url += `?${paramString}`
    }

    navigate(url, { replace: true })
  }

  const getCurrentPage = () => {
    return (currentOffset + limit) / limit
  }

  const selectPagination = (page, pageSize) => {
    var newOffset = limit * (page - 1)
    if (pageSize != limit) {
      // Page size changed, so reset the offset
      newOffset = 0
    }
    setLimit(pageSize)
    updateResults(selectedStage, selectedStaff, newOffset, showHiddenStages, pageSize)
  }

  const selectStage = (stage) => {
    updateResults(stage, selectedStaff, 0)
    onUpdateEventFilters(stage, tableDisplay, selectedStaff)
  }

  const slideLeft = () => {
    const slider = document.getElementById("slider")
    slider.scrollLeft = slider.scrollLeft - 300
  }

  const slideRight = () => {
    const slider = document.getElementById("slider")
    slider.scrollLeft = slider.scrollLeft + 300
  }

  const slideToElement = (id) => {
    const slider = document.getElementById("slider")
    const element = document.getElementById(id)
    if (!isNull(slider) && !isNull(element)) {
      const sliderPosition = slider.getBoundingClientRect().left
      const elementPosition = element.getBoundingClientRect().left
      const newPosition = elementPosition - sliderPosition - 50
      slider.scrollTo({left: newPosition, behavior: 'smooth'})
    }
  }

  const onUpdateStage = (event) => {
    setSelectedEvent(event)
    editStageForm.setFieldsValue({ stage_id: event.stage_id })
    setUpdateStageModalVisible(true)
  }

  const cancelUpdateStage = () => {
    setUpdateStageModalVisible(false)
  }

  const onUpdateStageSubmit = async (values) => {
    try {
      // Ignore if the event stage hasn't changed
      if (selectedEvent.stage_id == values["stage_id"]) {
        setUpdateStageModalVisible(false)
        return
      }

      await sendRequest(updateEventStage(selectedEvent.event_id, { stage_id: values["stage_id"] }));
      await refreshEventStages(showHiddenStages)
      updateResults(selectedStage, selectedStaff, currentOffset)
      setUpdateStageModalVisible(false)
    } catch {
      setUpdateStageModalVisible(false)
    }
  }

  const onOpenFilter = () => {
    if (selectedStaff.length > 0) {
      setTempSelectedStaff(selectedStaff)
    } else {
      selectAllStaff()
    }
    setFilterOpen(true)
  }

  const onSelectStaff = (id) => {
    var cloneSelected = cloneDeep(tempSelectedStaff)
    if (cloneSelected.includes(id)) {
      cloneSelected = cloneSelected.filter(x => x != id)
    } else {
      cloneSelected.push(id)
    }
    setTempSelectedStaff(cloneSelected)
  }

  const selectAllStaff = () => {
    var newSelectedStaff = employees.map(x => x.user_id)
    newSelectedStaff.push(1)
    setTempSelectedStaff(newSelectedStaff)
  }

  const onSubmitFilter = () => {
    var finalSelectedStaff = []
    if (tempSelectedStaff.length == 0) {
      finalSelectedStaff = [auth.user.user_id]
    } else if (tempSelectedStaff.length <= employees.length) {
      finalSelectedStaff = tempSelectedStaff
    }
    updateResults(selectedStage, finalSelectedStaff, currentOffset)
    onUpdateEventFilters(selectedStage, tableDisplay, finalSelectedStaff)
    setFilterOpen(false)
  }

  const onUpdateTableDisplay = (value) => {
    setTableDisplay(value)
    onUpdateEventFilters(selectedStage, value, selectedStaff)
  }

  const onUpdateEventFilters = (stage, display, staff = []) => {
    const filters = {
      selected_staff: staff,
      stage: stage,
      table_display: display
    }
    setEventFilters(auth.user.user_id, filters)
  }

  const onToggleHiddenStages = async () => {
    try {
      const newValue = !showHiddenStages
      setShowHiddenStages(newValue)
      await refreshEventStages(newValue)
      if (selectedStage == "all") {
        await updateResults(selectedStage, selectedStaff, 0, newValue)
      }
    } catch {}
  }

  const downloadPDF = async () => {
    try {
      const body = {
        stage: selectedStage,
        offset: 0,
        limit: 1000000,
        staff: selectedStaff,
        show_hidden: showHiddenStages
      }
      // Fetch records
      const response = await sendRequest(getEventsV2(body))
      const events = response.results;
      // Render and save PDF
      const blob = await pdf(( renderPDF(events) )).toBlob();
      saveAs(blob, `events-${moment().format("YYYYMMDDHHmmss")}.pdf`)
      // Show success
      notification.success({
        message: 'Success!',
        description: 'Your PDF has been downloaded. Please check your download folder.',
        duration: 3
      });
    } catch {
      notification.error({
        message: 'Error!',
        description: 'There was a problem generating your PDF. Please try again.',
        duration: 3
      });
    }
  }

  const renderPDF = (events) => {
    const type = tableDisplay == "list" ? "summary" : "detailed"
    return (
      <Document>
        <EventListPDF events={events} account_settings={accountSettings} type={type}/>
      </Document>
    )
  }

  const menu = (event) => {
    const viewEditText = canEditEventDetails ? "View/Edit Event" : "View Event"
    return (
      <Menu>
        { canEditEventDetails && (
          <Menu.Item>
            <div onClick={() => onUpdateStage(event)}>
              <MdChecklist style={{ marginRight: 8}}/> Update Stage
            </div>
          </Menu.Item>
        )}
        <Menu.Item>
          <div onClick={() => navigate(`/admin/events/${event.event_id}`)}>
            <EditOutlined style={{ marginRight: 8}}/> {viewEditText}
          </div>
        </Menu.Item>
        { canCreateEvents && (
          <Menu.Item>
            <div onClick={() => removeEvent(event)}>
              <DeleteOutlined style={{ marginRight: 8}}/> Delete
            </div>
          </Menu.Item>
        )}
      </Menu>
    )
  };

  const renderRightIcon = (event) => {
    return (
      <div className="display-flex mr-5" onClick={(e) => e.stopPropagation()}>
        <Dropdown overlay={menu(event)} placement="bottomRight" trigger="click">
          <div className="dots-container">
            <MdOutlineMoreHoriz style={{ fontSize: 24, color: '#999'}}/>
          </div>
        </Dropdown>
      </div>
    )
  }

  const renderTableHeaderIcons = () => {
    return (
      <div className="display-flex flex-middle">
        { canViewAllEvents && (
          <span style={{ position: "relative", userSelect: 'none' }} onClick={() => onOpenFilter()}>
            <MdFilterList size={28} className={`c-text-gray mr-5`}/>
            { selectedStaff.length > 0 && (
              <span style={{ position: "absolute", height: 12, paddingRight: 4, paddingLeft: 4, right: 5, top: 0, backgroundColor: "red", color: "#FFFFFF", fontSize: 8, borderRadius: 6 }}>{selectedStaff.length}</span>
            )}
          </span>
        )}
        <SearchOutlined className={`c-text-gray mr-10 fs-20`} onClick={() => navigate("/admin/events/search")}/>
        <FiDownload className={`c-text-gray mr-20 fs-20`} onClick={() => downloadPDF()}/>
        <div className="" style={{ height: 20, border: '1px solid #AEAEAE', marginRight: 20 }}></div>
        <HiOutlineViewList size={28} className={`${ tableDisplay == "list" ? "c-blue" : "c-text-gray"} mr-5`} onClick={() => onUpdateTableDisplay("list")}/>
        <MdOutlineGridView size={24} className={`${ tableDisplay == "grid" ? "c-blue" : "c-text-gray"} mr-5`} onClick={() => onUpdateTableDisplay("grid")}/>
      </div>
    )
  }

  const renderEventGroupHeader = (group, index) => {
    return (
      <div className="display-flex pl-10 mb-10 flex-middle">
        <div className="flex-1">
          <div className="fs-14 fw-700">{group.label}</div>
        </div>
        { index == 0 && renderTableHeaderIcons() }
      </div>
    )
  }

  const renderEventGroups = (group, index) => {
    return (
      <div key={index}>
        { renderEventGroupHeader(group, index)}
        <div className="shadow-card mb-10">
          { group.events.map((item, i) => renderEventCard(item, i, group.events.length)) }
        </div>
      </div>
    )
  }

  const renderEventGridGroups = (group, index) => {
    return (
      <div key={index} className="">
        { renderEventGroupHeader(group, index)}
        { group.events.map((item, i) => renderEventDetailedCard(item, i)) }
      </div>
    )
  }

  const renderEventCard = (event, index, totalGroupEvents) => {
    var showBorder = index < totalGroupEvents - 1
    return (
      <EventCard 
        event={event} 
        accountSettings={accountSettings} 
        rightIcon={renderRightIcon(event)} 
        className={showBorder ? "b-border" : ""} 
        onClick={() => navigate(`/admin/events/${event.event_id}`)} 
        showType={true} 
        showStatus={true}
        key={index}/>
    );
  }

  const renderClient = (client, index) => {
    return (
      <div key={index}  style={{ marginTop: 2, marginBottom: 2 }}>
        <div className="display-flex flex-middle fw-600">
          <div className="display-flex mr-10">
            <div className="user-profile-circle x-small"><UserOutlined/></div>
          </div>
          {client.first_name} {client.last_name} <span className="c-text-gray ml-5 fw-500">({client.role_name})</span>
        </div>
      </div>
    )
  }

  const renderStaff = (staff, index) => {
    return (
      <div key={index}  style={{ marginTop: 2, marginBottom: 2 }}>
        <div className="display-flex flex-middle fw-600">
          <div className="display-flex mr-10">
            <div className="user-profile-circle x-small"><UserOutlined/></div>
          </div>
          {staff.first_name} {staff.last_name} <span className="c-text-gray ml-5 fw-500">({staff.role_name})</span>
        </div>
      </div>
    )
  }

  const renderEventDetailedCard = (event, index) => {
    const eventDate = moment(event.event_date, "YYYY-MM-DD")
    const eventDay = eventDate.format("dddd").toUpperCase();
    const eventMonthDay = formatEventDayMonth(event.event_date, accountSettings);
    const eventYear = eventDate.format("YYYY");
    const stageName = !isNull(event.stage_id) ? toUpper(event.stage_name) : "UNKNOWN"
    const stageColor = !isNull(event.stage_id) ? event.stage_color : "gray"

    var venueName = ""
    var subText = ""
    if (isArray(event.venues) && event.venues.length > 0) {
      const venue = event.venues[0]
      if (venue.city) {
        const cityState = venue.state ? `${venue.city}, ${venue.state}` : venue.city
        venueName = `${venue.venue_name} (${cityState})`
      } else {
        venueName = venue.venue_name
      }
      subText = screens.md ? `${event.account_event_type_name} | ${venueName}` : venueName
    } else {
      subText = event.account_event_type_name
    }

    return (
      <div className="shadow-card mb-10 hover" key={index} onClick={() => navigate(`/admin/events/${event.event_id}`)}>
      <div className="ph-20 pv-15">
        <Row align="middle" gutter={[15,15]} wrap={false}>
          { screens.sm && (
            <Col flex={0} onClick={() => {}}>
              { isEmpty(event.event_photo) ? (
                <div className="bg-gray flex-1 flex-middle display-flex justify-content-center flex-col" style={{ width: 100, height: 100 }}>
                  <div className="fs-12 fw-600 line-1-2">{eventDay}</div>
                  <div className="c-blue fs-20 fw-700 line-1-2">{eventMonthDay}</div>
                  <div className="fs-16 fw-600 line-1-2">{eventYear}</div>
                </div>
              ) : (
                <Image src={event.event_photo} style={{ width: 100, height: 100, objectFit: 'cover' }}/>
              )}
            </Col>
          )}
          { screens.lg ? (
            <Col flex={1} onClick={() => {}}>
              <div className="fw-700">{event.event_name}</div>
              <div className="">{ formatEventDateLong(event.event_date, accountSettings) }</div>
              <div className="c-text-gray">{subText}</div>
            </Col>
          ) : (
            <Col flex={1} onClick={() => {}}>
              <div className="fw-700">{event.event_name}</div>
              <div className="">{ formatEventDateLong(event.event_date, accountSettings) }</div>
              <div className="c-text-gray mb-5">{subText}</div>
              <StatusTag status={stageName} color={stageColor}/>
            </Col>
          )}
          { screens.lg && (
            <Col flex={0}>
              <StatusTag status={stageName} color={stageColor}/>
            </Col>
          )}
          <Col flex={0}>
            <div className="display-flex" style={{ justifyContent: 'flex-end'}} onClick={(e) => e.stopPropagation()}>
              <Dropdown overlay={menu(event)} placement="bottomRight" trigger="click">
                <div className="dots-container">
                  <MdOutlineMoreHoriz style={{ fontSize: 24, color: '#999'}}/>
                </div>
              </Dropdown>
            </div>
          </Col>
        </Row>
      </div>
      <div className="ph-30 pb-15">
        <Row gutter={[15,15]} className="bg-gray p-15 radius-8">
          <Col xs={24} sm={12}>
            <div className="fs-10 c-text-gray">CLIENTS</div>
            { isArray(event.clients) && event.clients.map((x,i) => renderClient(x,i))}
            { ((isArray(event.clients) && event.clients.length == 0) || isNull(event.clients)) && (
              <div>--</div>
            )}
          </Col>
          <Col xs={24} sm={12}>
            <div className="fs-10 c-text-gray">ASSIGNED STAFF</div>
            { isArray(event.employees) && event.employees.map((x,i) => renderStaff(x,i))}
            { ((isArray(event.employees) && event.employees.length == 0) || isNull(event.employees)) && (
              <div>--</div>
            )}
          </Col>
        </Row>
      </div>
    </div>
    );
  }

  const renderHeader = () => {
    return (
      <Row align="middle" className="p-20">
        <Col flex={1}>
          <div className="fs-24 fw-700">Events</div>
        </Col>
        { canCreateEvents && (
          <Col flex={0}>
            <button className="page-title-button" onClick={() => navigate("/admin/events/new")}>New Event</button>
          </Col>
        )}
      </Row>
    )
  }

  const renderEventGrid = () => {
    return (
      <>
        { getGroupedEvents().map((group,i) => renderEventGridGroups(group,i)) }
      </>
    )
  }

  const renderEventsRows = () => {
    return (
      <>
        { getGroupedEvents().map((group,i) => renderEventGroups(group,i)) }
      </>
    )
  }

  const renderNoEventsText = () => {
    if (selectedStage == "all" && selectedStaff.length == 0) {
      return (
        <>
          <div className="fs-14 fw-500 c-text-gray">
            It looks like you don't have any events yet!
          </div>
          { canCreateEvents && (
            <button className="small-primary-button mt-10" onClick={() => navigate("/admin/events/new")}>New Event</button>
          )}
        </>
      )
    } else if (selectedStaff.length > 0) {
      return (
        <div className="fs-14 fw-500 c-text-gray">
          There are no events in this stage for the selected staff member(s).
          <div>Update your filter to try again.</div>
        </div>
      )
    } else {
      return (
        <div className="fs-14 fw-500 c-text-gray">
          Select another stage to view your events.
        </div>
      )
    }
  }

  const renderEvents = () => {
    if (isContentLoading) {
      return (
        <>
          <div className="display-flex">
            <div className="flex-1"></div>
            { renderTableHeaderIcons() }
          </div>
          <div className="ph-20 pv-80 text-center shadow-card mt-10">
            <LoadingSpinner/>
          </div>
        </>
      )
    }
    if (getGroupedEvents().length == 0) {
      return (
        <>
          <div className="display-flex">
            <div className="flex-1"></div>
            { renderTableHeaderIcons() }
          </div>
          <div className="ph-20 pv-40 text-center shadow-card mt-10">
            <div>
              <img src={emptyStateImage} width={200}/>
            </div>
            <div className="fs-18 fw-700 mt-30">
              No events in this stage
            </div>
            { renderNoEventsText() }
          </div>
        </>
      )
    }
    return (
      <>
        { tableDisplay == "grid" ? renderEventGrid() : renderEventsRows() }
        { totalRecords > limit && (
          <div className="mt-15 text-right">
            <Pagination defaultCurrent={1} defaultPageSize={limit} pageSize={limit} current={getCurrentPage()} total={totalRecords} onChange={(page,pageSize) => selectPagination(page, pageSize)}/>
          </div>
        )}
      </>
    )
  }

  const renderFilterModal = () => {
    return (
      <Modal visible={isFilterOpen} footer={null} onCancel={() => setFilterOpen(false)} width={400} closable={false} maskClosable={false} wrapClassName="rounded-modal">
        <Row align="middle">
          <Col flex={1}>
            <div className="fw-700 fs-18">Filters</div>
          </Col>
          <Col>
            <div className="display-flex" onClick={() => setFilterOpen(false)}><MdOutlineClose size={30} color={"#CCC"}/></div>
          </Col>
        </Row>
        <div className="">
          <Row gutter={[15,15]} className="mt-20">
            <Col xs={24}>
              <Row>
                <Col flex={1}>
                  <div className="fw-700 mb-5">Show Events Assigned To</div>
                </Col>
                <Col flex={0}>
                  <div className="blue-link" onClick={() => selectAllStaff()}>Select All</div>
                </Col>
              </Row>
              { employees.map((user, index) => (
                <div className="fs-14 mb-5 user-select-none" key={index}>
                  <Checkbox checked={tempSelectedStaff.includes(user.user_id)} value={true} onChange={(e) => onSelectStaff(user.user_id)}>{user.first_name} {user.last_name}</Checkbox>
                </div>
              ))}
               <div className="fs-14 mb-5 user-select-none">
                <Checkbox checked={tempSelectedStaff.includes(1)} value={true} onChange={(e) => onSelectStaff(1)}>Unassigned</Checkbox>
              </div>
            </Col>
          </Row>
          <button className="primary-button mt-30" type="button" onClick={() => onSubmitFilter()}>Apply Filters</button>
          <div className="text-center mt-15">
            <div className="blue-link" onClick={() => setFilterOpen(false)}>Cancel</div>
          </div>
        </div>
      </Modal>
    )
  }

  const renderConfirmRemoveModal = () => {
    return (
      <Modal visible={isConfirmRemoveVisible} closable={false} footer={null} width={400} wrapClassName="rounded-modal">
        <div className="text-right" onClick={() => setConfirmRemoveVisible(false)}><MdOutlineClose size={30} color={"#CCC"}/></div>
        <div className="mt-10">
          <div className="fw-700 fs-20 text-center">Delete Event</div>
          <div className="fw-500 fs-14 mt-20 mb-20 text-center">Are you sure you would like to delete the event <span className="fw-700">{selectedEvent.event_name}</span>? All data will be removed and any clients that are assigned to this event will no longer have access.</div>
          { isDeleting ? (
            <button className="primary-button warning" type="button"><LoadingOutlined/></button>
          ) : (
            <button className="primary-button warning" type="button" onClick={() => confirmDeleteEvent()}>Delete Event</button>
          )}
          { isDeleteError && (
            <div className="text-center mt-5 c-red">Something went wrong. Please try again.</div>
          )}
          <div className="text-center mt-15">
            <div className="blue-link" onClick={() => setConfirmRemoveVisible(false)}>Cancel</div>
          </div>
        </div>
      </Modal>
    )
  }

  const renderStageCard = (stage, index) => {
    const selectedClass = selectedStage == stage.stage_id ? "selected" : ""
    const allEventsStageClass = stage.stage_id == "all" ? "all-stage" : ""
    return (
      <>
        <div className={`event-status-card ${selectedClass} ${allEventsStageClass}`} key={index} onClick={() => selectStage(stage.stage_id)} id={stage.stage_id}>
          <div className="event-status-card--label">
            {stage.stage_name.toUpperCase()}
          </div>
          <div className="event-status-card--data">{ stage.event_count }</div>
        </div>
      </>
    )
  }

  const renderEventStageSlider = () => {
    return (
      <div className="event-status-container" style={{ width: `${sliderWidth}px` }}>
        { eventStages.length > 3 && (
          <div className="event-status-arrow-container mr-5" onClick={slideLeft}>
            <MdChevronLeft size={40} className="event-status-arrow"/>
          </div>
        )}
        <div className="event-status-slider" id="slider">
          { eventStages.map((x,i) => renderStageCard(x,i))}
        </div>
        { eventStages.length > 3 && (
          <div className="event-status-arrow-container ml-5" onClick={slideRight}>
            <MdChevronRight size={40} className="event-status-arrow"/>
          </div>
        )}
      </div>
    )
  }

  const renderUpdateStageModal = () => {
    return (
      <Modal visible={isUpdateStageModalVisible} closable={false} footer={null} width={600} wrapClassName="rounded-modal">
        <Row align="middle" className="b-border pb-10">
          <Col flex={1}>
            <div className="fw-700 fs-18">Update Stage</div>
          </Col>
          <Col>
            <div className="display-flex" onClick={() => cancelUpdateStage()}><MdOutlineClose size={30} color={"#CCC"}/></div>
          </Col>
        </Row>
        <div className="mt-10">
          <Form form={editStageForm} layout="vertical" name="edit-client-role" onFinish={onUpdateStageSubmit}>
            <Row gutter={[20,10]}>
              <Col xs={24} md={24}>
                { renderSelectField("Event Stage", "stage_id", "Select a stage...", eventStagesData, true) }
              </Col>
            </Row>
            <div className="admin-modal-footer">
              <button className="page-title-button" type="submit">Save</button>
            </div>
          </Form>
        </div>
      </Modal>
    )
  }

  const renderContent = () => {
    if (isLoading) {
      return <LoadingSpinner/>;
    }
    if (isError) {
      return <ErrorCard/>
    }
    const showHiddenText = hiddenStageCount > 0
    var hiddenStageText = ""
    if (showHiddenStages) {
      hiddenStageText = `Showing all hidden stages `
    } else {
      hiddenStageText = `${hiddenStageCount} hidden stage${hiddenStageCount == 1 ? "" : "s"} `
    }
    return (
      <>
        <FloatingContainer className="ph-20 bg-white b-border" verticalPadding={20} maxWidth={1000}>
          { renderEventStageSlider() }
          <div className="text-right" style={{ marginRight: 40 }}>
            { canViewEventStageSettings && (
              <>
                { showHiddenText && (
                  <>
                    <span className="c-text-gray fs-12">{hiddenStageText}</span>
                    <div className="gray-link" onClick={() => onToggleHiddenStages()}>({ showHiddenStages ? "hide" : "show"})</div>
                    <span className="c-text-gray fs-12"> | </span>
                  </>
                )}
                <div className="gray-link" onClick={() => navigate("/admin/setup/event-settings?tab=event-stages")}>Settings</div>
              </>
            )}
          </div>
          {/* <div className="mt-10">
            <Input className="list-search-input-square" size="large" placeholder="Event name, date, or client" value={searchText} onChange={(e) => console.log(e.target.value)} allowClear={true} prefix={<SearchOutlined color="#e4e4e4" style={{ marginRight: 6 }}/>} />
          </div> */}
        </FloatingContainer>
        <FloatingContainer className="ph-20" verticalPadding={20} maxWidth={1000}>
          { renderEvents() }
          { renderFilterModal() }
          { renderConfirmRemoveModal() }
          { renderUpdateStageModal() }
        </FloatingContainer>
      </>
    )
  }

  return (
    <AdminContent body={renderContent()} header={renderHeader()}/>
  );
}

export default EventsPage;
